import { useState } from "react";
import { useTransactionsFilters } from "../../hooks/useTransactionsFilters";
import { RestoredFiltersBanner } from "../../components/RestoredFiltersBanner";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { formatMoney } from "../../lib/api";
import { MoneyAmount } from "../../components/MoneyAmount";
import { LineItemsView } from "../../components/LineItemsView";
import { TagChips, TagsInput } from "../../components/Tags";
import { PAYMENT_ICONS, pickIcon } from "../../lib/typeIcons";
import { usePaymentsInfinite, usePaymentsMutations, useProfile, useFxRates, useTopTags, useCashAccounts, useDebts } from "../../hooks/queries";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, Pencil, CalendarClock, RotateCw, Link2, Wallet, CreditCard, AlertTriangle, AlertCircle, Calendar as CalendarIcon, Coins, Search, X } from "lucide-react";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { GroupedListView } from "../../components/GroupedListView";
import { nextRecurringDate, formatNextDate } from "../../lib/recurring";
import { pushHighlight } from "../../lib/highlights";
import { reportValidationError } from "../../lib/validationTelemetry";
import { HighlightCard } from "../../components/HighlightCard";
import { AccountPickerDialog } from "../../components/AccountPickerDialog";
import { PageHeader } from "../../components/PageHeader";
import { SwipeActions } from "../../components/SwipeActions";
import { AccountChip } from "../../components/AccountChip";

export default function Payments() {
  const { t, lang } = useLang();
  const isEs = lang === "es";
  const navigate = useNavigate();
  const {
    search, setSearch,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    tag: tagFilter, setTag: setTagFilter,
    account: accountFilter, setAccount: setAccountFilter,
    groupBy, setGroupBy,
    restored: filtersRestored,
    clear: clearFilters,
    hasActive: filtersActive,
  } = useTransactionsFilters({ persistKey: "pagos.filters" });
  // Pagos realizados (status=paid) ya no se listan aquí: viven en
  // Movimientos. Forzamos status="pending" para que esta pantalla
  // muestre solo lo accionable (lo aún por pagar). Las recurrentes
  // mantienen status="pending" después de pagar (avanza due_date)
  // así que siguen apareciendo de forma natural.
  // iter-189-fix-2 — match the Movements grouping UX so users can
  // collapse a long pending list by day / week / month.
  const debouncedSearch = useDebouncedValue(search, 350);
  const { items, sentinelRef, hasMore, isFetchingNextPage, isLoading } = usePaymentsInfinite({
    limit: 30,
    search: debouncedSearch || undefined,
    status: "pending",
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    tag: tagFilter || undefined,
  });
  // iter-195 — client-side quick filter chips (Atrasados / Próximos 7 días).
  const [quickFilter, setQuickFilter] = useState(null);
  // iter-211.2 — Account filter, injected into GroupedListView via `extraFilters`.
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const next7Ms = todayMs + 7 * 86400000;
  const filteredItems = items.filter((p) => {
    if (accountFilter && p.account_id !== accountFilter) return false;
    if (!quickFilter) return true;
    const dueMs = new Date(p.due_date || p.date).setHours(0, 0, 0, 0);
    if (quickFilter === "overdue") return dueMs < todayMs;
    if (quickFilter === "next7") return dueMs >= todayMs && dueMs <= next7Ms;
    return true;
  });
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const { create, update, remove: removeMut, markPaid, postpone: postponeMut } = usePaymentsMutations();
  const { data: tagsData } = useTopTags();
  const tagSuggestions = (tagsData?.by_kind?.payments || []).map((r) => r.tag);
  const { data: cashAccounts = [] } = useCashAccounts();
  const { data: debts = [] } = useDebts();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // iter-169zze · field-level error map so the user sees WHICH input
  // is missing instead of a generic "Fill all required fields" toast.
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    concept: "", amount: "", due_date: "", type: "personal", priority: "medium",
    recurring: false, frequency: "monthly", end_date: "",
    original_currency: "", notes: "", tags: [], account_id: "", debt_id: "",
    fundingMode: "account",  // "account" | "debt"
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { data: fxInfo } = useFxRates();
  const fxList = Object.keys(fxInfo?.rates || {}).sort();

  const [postponeOpen, setPostponeOpen] = useState(false);
  const [postponeTarget, setPostponeTarget] = useState(null);
  const [postponeDate, setPostponeDate] = useState("");

  // Account picker — fires when the user clicks "Marcar pagado" on a
  // payment that doesn't have a funding account set. See AccountPickerDialog.
  const [pickerTarget, setPickerTarget] = useState(null);

  const openNew = () => {
    setEditing(null);
    setFieldErrors({});
    setForm({ concept: "", amount: "", due_date: "", type: "personal", priority: "medium", recurring: false, frequency: "monthly", end_date: "", original_currency: "", notes: "", tags: [], account_id: "", debt_id: "", fundingMode: "account" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setFieldErrors({});
    setForm({
      concept: p.concept,
      amount: String(p.original_amount || p.amount),
      due_date: p.due_date ? p.due_date.split("T")[0] : "",
      type: p.type,
      priority: p.priority,
      recurring: !!p.recurring,
      frequency: p.frequency || "monthly",
      end_date: p.end_date ? p.end_date.split("T")[0] : "",
      original_currency: p.original_currency || "",
      notes: p.notes || "",
      tags: Array.isArray(p.tags) ? p.tags : [],
      account_id: p.account_id || "",
      debt_id: p.debt_id || "",
      fundingMode: p.debt_id ? "debt" : "account",
    });
    setOpen(true);
  };

  const save = async () => {
    // iter-169zze — track field-level errors so the UI can highlight
    // the offending inputs instead of toasting a vague message.
    const fe = {};
    if (!form.concept?.trim()) {
      fe.concept = isEs ? "El concepto es obligatorio." : "Concept is required.";
    }
    if (!form.amount || Number(form.amount) <= 0) {
      fe.amount = isEs ? "Indica un monto mayor a 0." : "Enter an amount greater than 0.";
    }
    if (!form.due_date) {
      fe.due_date = isEs ? "Elige la fecha de vencimiento." : "Pick the due date.";
    }
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      reportValidationError("pay-form", fe);
      toast.error(isEs ? "Faltan campos obligatorios." : "Missing required fields.");
      return;
    }
    setFieldErrors({});
    const customCurrency = form.original_currency && form.original_currency !== currency;
    const payload = {
      concept: form.concept,
      amount: Number(form.amount),
      due_date: new Date(form.due_date).toISOString(),
      type: form.type,
      priority: form.priority,
      status: editing?.status || "pending",
      recurring: !!form.recurring,
      frequency: form.recurring ? form.frequency : null,
      end_date: form.recurring && form.end_date ? new Date(form.end_date).toISOString() : null,
      ...(customCurrency ? { original_currency: form.original_currency } : {}),
      notes: form.notes,
      tags: form.tags,
      // Mutually exclusive: only the active funding mode gets sent.
      account_id: form.fundingMode === "account" ? (form.account_id || null) : null,
      debt_id: form.fundingMode === "debt" ? (form.debt_id || null) : null,
    };
    try {
      if (editing) {
        const updated = await update.mutateAsync({ id: editing.id, payload });
        pushHighlight("payment", updated?.id || editing.id);
      } else {
        const created = await create.mutateAsync(payload);
        pushHighlight("payment", created?.id);
      }
      toast.success("Saved");
      setOpen(false);
    } catch (e) { console.error("save payment failed", e); /* onError toast already fired */ }
  };

  const pay = async (p) => {
    // When the payment has no funding account AND is not debt-funded
    // (debt_id set ⇒ charge the debt) AND is not the auto-payment of a
    // debt (source_debt_id ⇒ funded through the linked account or primary),
    // we MUST ask the user which cash account funds this — otherwise the
    // silent fallback to the primary account creates untraceable drift
    // across wallets in different currencies.
    if (!p.account_id && !p.debt_id) {
      setPickerTarget(p);
      return;
    }
    await runPay(p, null);
  };

  const runPay = async (p, funding) => {
    // ``funding`` is either null (legacy: payment already has account_id /
    // debt_id) OR ``{account_id}`` OR ``{debt_id}`` from the picker dialog.
    try {
      pushHighlight("payment", p.id);
      pushHighlight("movement_src_payment", p.id);
      const arg = funding
        ? { id: p.id, ...funding }
        : p.id;
      await markPaid.mutateAsync(arg);
      toast.success(
        tx(lang,
          `✓ ${p.concept} paid · now in Movements`,
          `✓ ${p.concept} pagado · ahora en Movimientos`,
          `✓ ${p.concept} pago · agora em Movimentos`,
        ),
        {
          duration: 5000,
          action: {
            label: tx(lang, "View", "Ver", "Ver"),
            onClick: () => navigate("/history"),
          },
        },
      );
    } catch (e) { console.error("mark paid failed", e); /* onError toast already fired */ }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await removeMut.mutateAsync(deleteTarget.id);
      toast.success("Deleted");
      setDeleteTarget(null);
    } catch (e) {
      console.error("delete payment failed", e);
      toast.error("Delete failed");
    }
  };

  const postpone = async () => {
    try {
      await postponeMut.mutateAsync({
        id: postponeTarget.id,
        new_due_date: new Date(postponeDate).toISOString(),
      });
      toast.success("Postponed");
      setPostponeOpen(false);
    } catch (e) { console.error("postpone failed", e); /* onError toast already fired */ }
  };

  const prioColor = (p) => ({
    high: "bg-red-50 text-red-700 ring-red-200",
    medium: "bg-amber-50 text-amber-700 ring-amber-200",
    low: "bg-slate-100 text-slate-600 ring-slate-200",
  }[p]);

  // iter-195 — renderRow extracted so the page consumes GroupedListView.
  const renderPaymentRow = (p) => {
    const PayIcon = pickIcon(PAYMENT_ICONS, p.type);
    const isManaged = !!p.source_debt_id;
    const isDcaContribution = !!p.source_investment_id;
    return (
      <SwipeActions
        key={p.id}
        testid={`pay-swipe-${p.id}`}
        actions={(!isManaged && !isDcaContribution) ? [
          { label: tx(lang, "Edit", "Editar", "Editar"), icon: Pencil, color: "bg-sky-500", testid: `swipe-edit-pay-${p.id}`, onClick: () => openEdit(p) },
          { label: tx(lang, "Delete", "Eliminar", "Excluir"), icon: Trash2, color: "bg-rose-500", testid: `swipe-delete-pay-${p.id}`, onClick: () => setDeleteTarget(p) },
        ] : []}
      >
        <HighlightCard kind="payment" id={p.id} data-testid={`payment-${p.id}`} className="p-4 sm:p-5 rounded-2xl border-slate-200">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center">
                <PayIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                  {p.concept}
                  {p.recurring && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-sky-50 ring-1 ring-sky-200 rounded-full px-1.5 py-0.5">
                      <RotateCw className="h-2.5 w-2.5" /> {p.frequency || "monthly"}
                    </span>
                  )}
                  {isManaged && (
                    <Link
                      to="/debts"
                      onClick={() => pushHighlight("debt", p.source_debt_id)}
                      data-testid={`pay-managed-${p.id}`}
                      title={tx(lang, "Payment managed from its linked debt. Click to see it highlighted in /Debts.", "Pago gestionado desde la deuda vinculada. Click para verla resaltada en /Deudas.", "Pagamento gerenciado pela dívida vinculada. Clique para ver em /Dívidas.")}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-full px-1.5 py-0.5 hover:bg-rose-100 hover:ring-rose-300 transition-colors"
                    >
                      <Link2 className="h-2.5 w-2.5" /> {tx(lang, "Debt", "Deuda", "Dívida")}
                    </Link>
                  )}
                  {isDcaContribution && (
                    <Link
                      to="/investments"
                      onClick={() => pushHighlight("investment", p.source_investment_id)}
                      data-testid={`pay-dca-link-${p.id}`}
                      title={tx(lang, "Recurring contribution to an investment. Click to see it highlighted.", "Aporte recurrente a una inversión. Click para verla resaltada.", "Aporte recorrente para um investimento. Clique para ver destacado.")}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-1.5 py-0.5 hover:bg-emerald-100 hover:ring-emerald-300 transition-colors"
                    >
                      <Link2 className="h-2.5 w-2.5" /> {tx(lang, "Investment", "Inversión", "Investimento")}
                    </Link>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  Due {new Date(p.due_date).toLocaleDateString()} • {p.type}
                  {p.recurring && (() => {
                    const nxt = nextRecurringDate(p.due_date, p.frequency, p.end_date);
                    return nxt ? (
                      <span className="ml-2 text-sky-700" data-testid={`pay-next-${p.id}`}>
                        • {tx(lang, "next", "próximo", "próximo")} {formatNextDate(nxt, lang)}
                      </span>
                    ) : null;
                  })()}
                </div>
                {p.notes && (
                  <div data-testid={`pay-notes-${p.id}`} className="text-[11px] text-slate-500 mt-0.5 italic truncate max-w-xs" title={p.notes}>
                    “{p.notes}”
                  </div>
                )}
                {Array.isArray(p.tags) && p.tags.length > 0 && (
                  <div className="mt-1.5"><TagChips tags={p.tags} testid={`pay-tags-${p.id}`} /></div>
                )}
                {/* iter-220.1 — account/debt funding chip. Hidden for
                    managed payments (source_debt_id) because they
                    already render a Link2 "Deuda" badge above. */}
                {!isManaged && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <AccountChip
                      item={p}
                      accounts={cashAccounts}
                      debts={debts}
                      lang={lang}
                      testid={`pay-account-chip-${p.id}`}
                      missingFallback={
                        <button /* design-system: ALLOW legacy-todo */
                          type="button"
                          onClick={() => openEdit(p)}
                          data-testid={`pay-account-missing-${p.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:border-emerald-300 dark:hover:border-emerald-800 hover:shadow-sm transition-all"
                        >
                          <Wallet className="h-3 w-3" />
                          {isEs ? "Asignar cuenta" : "Assign account"}
                        </button>
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-[11px] px-2 py-1 rounded-full font-bold uppercase ring-1 ${prioColor(p.priority)}`}>{p.priority}</span>
              <MoneyAmount
                amount={p.amount} currency={currency}
                original_currency={p.original_currency}
                original_amount={p.original_amount}
                fx_rate={p.fx_rate} fx_at={p.fx_at}
                size="md" testid={`pay-amount-${p.id}`}
              />
              {p.status === "pending" && (
                <>
                  <Button data-testid={`pay-btn-${p.id}`} size="sm" variant="outline" className="gap-1" onClick={() => pay(p)}>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t("mark_paid")}
                  </Button>
                  <Button data-testid={`postpone-btn-${p.id}`} size="sm" variant="ghost" className="gap-1" onClick={() => { setPostponeTarget(p); setPostponeDate(""); setPostponeOpen(true); }}>
                    <CalendarClock className="h-4 w-4" /> {t("postpone")}
                  </Button>
                </>
              )}
              {!isManaged && !isDcaContribution && (
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-pay-${p.id}`} className="hidden sm:inline-flex"><Pencil className="h-4 w-4" /></Button>
              )}
              {!isManaged && !isDcaContribution && (
                <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(p)} data-testid={`delete-pay-${p.id}`} className="hidden sm:inline-flex"><Trash2 className="h-4 w-4 text-red-600" /></Button>
              )}
            </div>
          </div>
          <LineItemsView items={p.line_items} amount={p.amount} currency={currency} reconciled={p.amount_reconciled} claimedAmount={p.claimed_amount} testid={`pay-items-${p.id}`} />
        </HighlightCard>
      </SwipeActions>
    );
  };

  // iter-195 — stat chips computed from currently-loaded items.
  let __statTotal = 0, __statOver = 0, __statOverAmt = 0, __statN7 = 0, __statN7Amt = 0;
  items.forEach((p) => {
    const amt = Number(p.amount) || 0;
    __statTotal += amt;
    const dueMs = new Date(p.due_date || p.date).setHours(0, 0, 0, 0);
    if (dueMs < todayMs) { __statOver += 1; __statOverAmt += amt; }
    else if (dueMs <= next7Ms) { __statN7 += 1; __statN7Amt += amt; }
  });
  const __fmt = (n) => formatMoney(n, currency, { compact: true });
  const statChips = items.length > 0 ? [
    { id: "total", icon: Coins, label: isEs ? "Total pendiente" : "Total pending", value: __fmt(__statTotal), tone: "slate" },
    { id: "overdue", icon: AlertCircle, label: isEs ? "Atrasados" : "Overdue", value: `${__statOver} · ${__fmt(__statOverAmt)}`, tone: "rose", filterId: __statOver > 0 ? "overdue" : null },
    { id: "next7", icon: CalendarClock, label: isEs ? "Próximos 7 días" : "Next 7 days", value: `${__statN7} · ${__fmt(__statN7Amt)}`, tone: "amber", filterId: __statN7 > 0 ? "next7" : null },
    { id: "count", icon: CalendarIcon, label: isEs ? "Pagos" : "Payments", value: items.length, tone: "emerald" },
  ] : [];

  return (
    <Layout>
      <PageHeader
        eyebrow={tx(lang, "Outflows", "Salidas", "Saídas")}
        title={t("nav_payments")}
        subtitle={tx(lang,
          `${items.length} pending — mark paid and they slide into Movements.`,
          `${items.length} pendientes — marca pagado y se mueven a Movimientos.`,
          `${items.length} pendentes — marque como pago e vão para Movimentos.`,
        )}
        testid="payments-header"
        actions={
          <Button data-testid="add-payment-btn" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl gap-2">
            <Plus className="h-4 w-4" /> {t("new_payment")}
          </Button>
        }
      />

      <GroupedListView
        testid="payments-list"
        lang={lang}
        items={filteredItems}
        isLoading={isLoading}
        renderRow={renderPaymentRow}
        dateAccessor={(p) => p.due_date || p.date}
        order="asc"
        restoredBanner={
          <RestoredFiltersBanner
            visible={filtersRestored && filtersActive}
            onClear={clearFilters}
            testid="payments"
            lang={lang}
          />
        }
        extraFilters={
          <Select
            value={accountFilter || "_all"}
            onValueChange={(v) => setAccountFilter(v === "_all" ? "" : v)}
          >
            <SelectTrigger
              className="h-9 w-[170px] text-xs"
              data-testid="pay-filter-account"
            >
              <SelectValue placeholder={tx(lang, "All accounts", "Todas las cuentas", "Todas as contas")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{tx(lang, "All accounts", "Todas las cuentas", "Todas as contas")}</SelectItem>
              {(cashAccounts || []).map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tx(lang, "Search concept…", "Buscar concepto…", "Buscar conceito…")}
        dateFrom={dateFrom} dateTo={dateTo}
        onDateFromChange={setDateFrom} onDateToChange={setDateTo}
        tagSuggestions={tagSuggestions}
        tagFilter={tagFilter}
        onTagFilterChange={setTagFilter}
        statChips={statChips}
        activeChip={quickFilter}
        onChipClick={setQuickFilter}
        sentinelRef={sentinelRef}
        hasMore={hasMore}
        isFetchingMore={isFetchingNextPage}
        // eslint-disable-next-line react/no-unstable-nested-components
        groupSummary={(groupItems) => {
          const sum = groupItems.reduce((a, p) => a + (Number(p.amount) || 0), 0);
          return (
            <span className="text-sm font-bold tabular-nums text-rose-700">
              -{formatMoney(sum, currency)}
            </span>
          );
        }}
        emptyState={
          <Card className="p-8 text-center text-slate-500 rounded-2xl border-dashed">
            {isEs ? "Aún no tienes pagos pendientes." : "No pending payments yet."}
          </Card>
        }
      />


      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        testid="payment-dialog"
        size="lg"
        title={editing ? t("edit") : t("new_payment")}
        description={editing ? t("edit") : t("new_payment")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="pay-form-save" onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{t("save")}</Button>
          </div>
        }
      >
        <div className="space-y-3">
            <div>
              <Label htmlFor="pay-form-concept" className="flex items-center gap-1">
                {t("concept")}
                <span className="text-rose-500" aria-hidden="true">*</span>
              </Label>
              <Input
                id="pay-form-concept"
                data-testid="pay-form-concept"
                value={form.concept}
                onChange={(e) => { setForm({ ...form, concept: e.target.value }); if (fieldErrors.concept) setFieldErrors({ ...fieldErrors, concept: undefined }); }}
                aria-invalid={fieldErrors.concept ? "true" : undefined}
                aria-describedby={fieldErrors.concept ? "pay-concept-err" : undefined}
                className={fieldErrors.concept ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
              />
              {fieldErrors.concept && (
                <p id="pay-concept-err" role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="pay-form-concept-error">
                  {fieldErrors.concept}
                </p>
              )}
            </div>
            <div className="grid grid-cols-[1fr_120px_140px] gap-3">
              <div>
                <Label htmlFor="pay-form-amount" className="flex items-center gap-1">
                  {t("amount")}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="pay-form-amount"
                  data-testid="pay-form-amount"
                  type="number"
                  value={form.amount}
                  onChange={(e) => { setForm({ ...form, amount: e.target.value }); if (fieldErrors.amount) setFieldErrors({ ...fieldErrors, amount: undefined }); }}
                  aria-invalid={fieldErrors.amount ? "true" : undefined}
                  aria-describedby={fieldErrors.amount ? "pay-amount-err" : undefined}
                  className={fieldErrors.amount ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
                />
                {fieldErrors.amount && (
                  <p id="pay-amount-err" role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="pay-form-amount-error">
                    {fieldErrors.amount}
                  </p>
                )}
              </div>
              <div>
                <Label>{t("currency") || "Currency"}</Label>
                <Select value={form.original_currency || currency} onValueChange={(v) => setForm({ ...form, original_currency: v === currency ? "" : v })}>
                  <SelectTrigger data-testid="pay-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(fxList.length ? fxList : [currency]).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pay-form-date" className="flex items-center gap-1">
                  {t("due_date")}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="pay-form-date"
                  data-testid="pay-form-date"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => { setForm({ ...form, due_date: e.target.value }); if (fieldErrors.due_date) setFieldErrors({ ...fieldErrors, due_date: undefined }); }}
                  aria-invalid={fieldErrors.due_date ? "true" : undefined}
                  aria-describedby={fieldErrors.due_date ? "pay-date-err" : undefined}
                  className={fieldErrors.due_date ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
                />
                {fieldErrors.due_date && (
                  <p id="pay-date-err" role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="pay-form-date-error">
                    {fieldErrors.due_date}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("type")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="pay-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["work", "personal", "debt", "recurring"].map((v) => {
                      const TIcon = pickIcon(PAYMENT_ICONS, v);
                      const label = v === "work" ? tx(lang, "Work", "Trabajo", "Trabalho")
                        : v === "personal" ? tx(lang, "Personal", "Personal", "Pessoal")
                        : v === "debt" ? tx(lang, "Debt", "Deuda", "Dívida")
                        : tx(lang, "Recurring", "Recurrente", "Recorrente");
                      return (
                        <SelectItem key={v} value={v}>
                          <span className="inline-flex items-center gap-2">
                            <TIcon className="h-4 w-4 text-slate-500" />
                            {label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("priority")}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="pay-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">{t("high")}</SelectItem>
                    <SelectItem value="medium">{t("medium")}</SelectItem>
                    <SelectItem value="low">{t("low")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input /* design-system: ALLOW legacy-todo */
                  data-testid="pay-form-recurring"
                  type="checkbox"
                  checked={form.recurring}
                  onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-slate-900">{t("recurring")}</span>
              </label>
              {form.recurring && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("frequency")}</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="pay-form-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">{t("weekly")}</SelectItem>
                        <SelectItem value="biweekly">{t("biweekly")}</SelectItem>
                        <SelectItem value="monthly">{t("monthly")}</SelectItem>
                        <SelectItem value="quarterly">{t("quarterly")}</SelectItem>
                        <SelectItem value="yearly">{t("yearly")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("end_date")}</Label>
                    <Input data-testid="pay-form-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                  </div>
                </div>
              )}
              {form.recurring && form.due_date && (() => {
                const nxt = nextRecurringDate(form.due_date, form.frequency, form.end_date);
                return nxt ? (
                  <p className="mt-2 text-xs text-sky-700" data-testid="pay-form-next-preview">
                    {tx(lang,
                      `Next occurrence: ${formatNextDate(nxt, lang)}`,
                      `Próxima ocurrencia: ${formatNextDate(nxt, lang)}`,
                      `Próxima ocorrência: ${formatNextDate(nxt, lang)}`,
                    )}
                  </p>
                ) : null;
              })()}
              <div className="mt-3">
                {/* Funding source — explicit toggle. Either:
                    • Cuenta de caja → debits the chosen wallet on mark_paid.
                    • Deuda (TC/préstamo) → INCREASES the chosen debt's balance.
                    Mutually exclusive in the API; we send only the active one. */}
                <Label>{tx(lang, "How will this be paid?", "¿Cómo se va a pagar?", "Como será pago?")}</Label>
                <div className="mt-1.5 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    data-testid="pay-form-funding-account"
                    onClick={() => setForm({ ...form, fundingMode: "account" })}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      form.fundingMode === "account"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Wallet className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                    {tx(lang, "Account", "Cuenta", "Conta")}
                  </button>
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    data-testid="pay-form-funding-debt"
                    onClick={() => setForm({ ...form, fundingMode: "debt" })}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      form.fundingMode === "debt"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <CreditCard className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                    {tx(lang, "Debt / Card", "Deuda / TC", "Dívida / Cartão")}
                  </button>
                </div>
                {form.fundingMode === "account" ? (
                  <div className="mt-2">
                    <Select
                      value={form.account_id || "__primary__"}
                      onValueChange={(v) => setForm({ ...form, account_id: v === "__primary__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="pay-form-account"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__primary__">
                          {tx(lang, "Primary account (auto)", "Cuenta principal (auto)", "Conta principal (auto)")}
                        </SelectItem>
                        {cashAccounts.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            <span className="inline-flex items-center gap-1.5">
                              <span>{a.name}</span>
                              <span className="text-[10px] uppercase tracking-wider text-slate-400">{a.currency}</span>
                              <span className="text-[10px] text-slate-500 tabular-nums">· {formatMoney(a.balance || 0, a.currency)}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-500 mt-1">
                      {tx(lang,
                        "When you mark this payment done, we'll debit this account.",
                        "Al marcar el pago como hecho, restaremos el monto de esta cuenta.",
                        "Quando você marcar como pago, debitamos o valor dessa conta.",
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Select
                      value={form.debt_id || ""}
                      onValueChange={(v) => setForm({ ...form, debt_id: v })}
                    >
                      <SelectTrigger data-testid="pay-form-debt">
                        <SelectValue placeholder={tx(lang, "Pick a debt / card", "Elige la deuda / TC", "Escolha a dívida / cartão")} />
                      </SelectTrigger>
                      <SelectContent>
                        {debts.length === 0 && (
                          <SelectItem value="_empty" disabled>
                            {tx(lang, "No debts — create one in /Debts", "No tienes deudas — crea una en /Deudas", "Sem dívidas — crie uma em /Dívidas")}
                          </SelectItem>
                        )}
                        {debts.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            <span className="inline-flex items-center gap-1.5">
                              <span>{d.name}</span>
                              <span className="text-[10px] uppercase tracking-wider text-slate-400">{d.currency}</span>
                              <span className="text-[10px] text-slate-500 tabular-nums">
                                · {tx(lang, "balance", "saldo", "saldo")} {formatMoney(d.balance || 0, d.currency)}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-amber-700 mt-1 inline-flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {tx(lang,
                        "When you mark this payment done, this debt's balance will INCREASE by the payment amount.",
                        "Al marcar el pago como hecho, esta deuda AUMENTARÁ por el monto del pago.",
                        "Quando você marcar como pago, essa dívida AUMENTARÁ pelo valor do pagamento.",
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <Label>{tx(lang, "Notes", "Notas", "Notas")}</Label>
                <textarea /* design-system: ALLOW legacy-todo */
                  data-testid="pay-form-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder={tx(lang,
                    "Free-form detail — reference, contact, reason…",
                    "Detalle libre — referencia, contacto, motivo…",
                    "Detalhe livre — referência, contato, motivo…",
                  )}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>
              <div className="mt-3">
                <Label>{tx(lang, "Tags", "Etiquetas", "Tags")}</Label>
                <TagsInput
                  value={form.tags}
                  onChange={(tags) => setForm({ ...form, tags })}
                  suggestions={tagSuggestions}
                  placeholder={tx(lang, "utilities, recurring…", "servicios, recurrente…", "serviços, recorrente…")}
                  testid="pay-form-tags"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  {tx(lang,
                    "You can also write #tag inline in the notes.",
                    "También puedes usar #etiqueta dentro de las notas.",
                    "Você também pode usar #tag dentro das notas.",
                  )}
                </p>
              </div>
            </div>
          </div>
      </BottomSheet>

      <BottomSheet
        open={postponeOpen}
        onOpenChange={setPostponeOpen}
        testid="postpone-dialog"
        size="sm"
        title={t("postpone")}
        description={t("postpone")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setPostponeOpen(false)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="postpone-save" onClick={postpone} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{t("save")}</Button>
          </div>
        }
      >
        <Label>{t("due_date")}</Label>
        <Input data-testid="postpone-date" type="date" value={postponeDate} onChange={(e) => setPostponeDate(e.target.value)} className="h-12 text-base rounded-xl mt-1.5" />
      </BottomSheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="pay-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_delete_pay", { name: deleteTarget?.concept || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pay-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="pay-delete-confirm"
              onClick={remove}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Funding-account picker — fires when the user marks paid a payment
          that has no account_id (and isn't debt-funded). Forces the
          explicit choice so wallet balances stay traceable. */}
      <AccountPickerDialog
        target={pickerTarget}
        mode="pay"
        cashAccounts={cashAccounts}
        lang={lang}
        onClose={() => setPickerTarget(null)}
        onSubmit={async (funding) => {
          const p = pickerTarget;
          setPickerTarget(null);
          await runPay(p, funding);
        }}
      />
    </Layout>
  );
}
